import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'

import { Button } from '../../../components/Button'
import { useAuth } from '../../AuthContext'
import {
  TRANSPORT_CONFIG_KEY,
  setFare,
  type Fare,
} from '../../api/transportConfig'
import { MAX_FARE_PESEWAS, ghsInput, parseGhs } from '../../transportUnits'
import { SavedNote } from './SavedNote'
import { errorMessage } from './errorMessage'
import { INPUT } from './styles'
import { useSyncedText } from './useSyncedText'

/**
 * One fare: typed in cedis, saved as pesewas, one PUT per save (so each change
 * is its own audit row). Empty means "not set", which keeps the service
 * unbookable; 0 is a real, free price. A failed save keeps what was typed; a
 * change made by someone else replaces the field unless it is being edited.
 */
export function FareCell({
  fare,
  label,
}: {
  fare: Fare
  label: string
}): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useSyncedText(ghsInput(fare.amount_pesewas))
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (pesewas: number | null) => setFare(token, fare.id, pesewas),
    onSuccess: () => {
      setProblem(null)
      setSaved(true)
      // The Save button goes once the field is clean: keep focus in the cell.
      input.current?.focus()
      void queryClient.invalidateQueries({ queryKey: TRANSPORT_CONFIG_KEY })
    },
    onError: (e) => setProblem(errorMessage(e)),
  })

  // Compared as amounts, so "12.5" after saving "12.50" is not a change.
  const typed = parseGhs(text)
  const dirty = !typed.ok || typed.pesewas !== fare.amount_pesewas

  function submit(e: FormEvent): void {
    e.preventDefault()
    if (!dirty) return
    if (!typed.ok) {
      setProblem('Use an amount like 12.50 (two decimals at most).')
      return
    }
    if (typed.pesewas !== null && typed.pesewas > MAX_FARE_PESEWAS) {
      setProblem('That is more than GHS 100,000. Check the amount.')
      return
    }
    save.mutate(typed.pesewas)
  }

  const errorId = `fare-${fare.id}-error`
  return (
    <form onSubmit={submit} className="flex min-w-0 flex-col gap-1">
      <span className="flex items-center gap-1.5">
        <span aria-hidden="true" className="text-xs text-ink-3">
          GHS
        </span>
        <input
          ref={input}
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setProblem(null)
            setSaved(false)
          }}
          placeholder="Not set"
          aria-label={`${label}, in cedis`}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? errorId : undefined}
          className={`${INPUT} w-24`}
        />
        {dirty && (
          <Button
            kind="secondary"
            type="submit"
            loading={save.isPending}
            aria-label={`Save ${label}`}
          >
            Save
          </Button>
        )}
        <SavedNote show={saved && !dirty} />
      </span>
      {problem && (
        <span id={errorId} role="alert" className="text-xs text-bed-bad">
          {problem}
        </span>
      )}
    </form>
  )
}
